/**
 * Document-level settings for a SOW template: the metadata and page setup that
 * apply to the whole exported document. Read-only chips in view mode; design-token
 * form controls in edit mode. State is owned by the workbench.
 */

import { NumberInput, PillRadio, Select, SelectItem, TextArea, TextField } from '@protoss/ui';
import * as React from 'react';
import type { SowDocumentSettings } from '../../lib/sowContract';

const FONT_OPTIONS = ['Arial', 'Helvetica', 'Calibri', 'Times New Roman', 'Georgia', 'Garamond'];
const TWIPS_PER_INCH = 1440;

function inches(twips: number): string {
  return `${+(twips / TWIPS_PER_INCH).toFixed(2)}`;
}

function Chip({ children }: { children: React.ReactNode }) {
  return <span className="ptp-sow-doc__chip">{children || '-'}</span>;
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="ptp-sow-doc__field">
      <span className="ptp-sow-doc__label">{label}</span>
      {children}
    </div>
  );
}

export interface SowDocumentSettingsPanelProps {
  settings: SowDocumentSettings;
  isEditing: boolean;
  onChange: (next: SowDocumentSettings) => void;
}

export function SowDocumentSettingsPanel({
  settings,
  isEditing,
  onChange,
}: SowDocumentSettingsPanelProps) {
  const set = <K extends keyof SowDocumentSettings>(key: K, value: SowDocumentSettings[K]) =>
    onChange({ ...settings, [key]: value });

  const setMargin = (side: keyof SowDocumentSettings['margins'], inchValue: string) => {
    const twips = Math.max(0, Math.round((parseFloat(inchValue) || 0) * TWIPS_PER_INCH));
    onChange({ ...settings, margins: { ...settings.margins, [side]: twips } });
  };

  const fontOptions = settings.defaultFont && !FONT_OPTIONS.includes(settings.defaultFont)
    ? [settings.defaultFont, ...FONT_OPTIONS]
    : FONT_OPTIONS;

  const marginSummary = `${inches(settings.margins.top)}″ top · ${inches(settings.margins.right)}″ right · ${inches(settings.margins.bottom)}″ bottom · ${inches(settings.margins.left)}″ left`;

  return (
    <section className="ptp-sow-doc" aria-label="Document settings">
      <header className="ptp-sow-doc__header">
        <span className="ptp-sow-doc__title">Document settings</span>
      </header>

      <div className="ptp-sow-doc__grid">
        <Field label="Title">
          {isEditing ? (
            <TextField
              value={settings.title}
              onChange={(e) => set('title', e.target.value)}
              placeholder="Statement of Work"
              size="compact"
            />
          ) : (
            <Chip>{settings.title}</Chip>
          )}
        </Field>

        <Field label="File name">
          {isEditing ? (
            <TextField
              value={settings.filename}
              onChange={(e) => set('filename', e.target.value)}
              placeholder="{{protoss:context.clientName}} SOW.docx"
              size="compact"
            />
          ) : (
            <Chip>{settings.filename}</Chip>
          )}
        </Field>

        <Field label="Author">
          {isEditing ? (
            <TextField
              value={settings.creator}
              onChange={(e) => set('creator', e.target.value)}
              size="compact"
            />
          ) : (
            <Chip>{settings.creator}</Chip>
          )}
        </Field>

        <Field label="Default font">
          {isEditing ? (
            <Select
              value={settings.defaultFont || ''}
              onValueChange={(v) => set('defaultFont', v)}
              placeholder="Default"
              size="sm"
            >
              {fontOptions.map((f) => (
                <SelectItem key={f} value={f}>
                  {f}
                </SelectItem>
              ))}
            </Select>
          ) : (
            <Chip>{settings.defaultFont}</Chip>
          )}
        </Field>

        <Field label="Orientation">
          {isEditing ? (
            <PillRadio
              options={[
                { value: 'portrait', label: 'Portrait' },
                { value: 'landscape', label: 'Landscape' },
              ]}
              selectedValue={settings.orientation}
              onSelectionChange={(v) =>
                set('orientation', v === 'landscape' ? 'landscape' : 'portrait')
              }
            />
          ) : (
            <Chip>{settings.orientation === 'landscape' ? 'Landscape' : 'Portrait'}</Chip>
          )}
        </Field>

        <Field label="Page margins">
          {isEditing ? (
            <div className="ptp-sow-doc__margins">
              {(['top', 'right', 'bottom', 'left'] as const).map((side) => (
                <label key={side} className="ptp-sow-doc__margin">
                  <span className="ptp-sow-doc__margin-label">{side}</span>
                  <NumberInput
                    value={inches(settings.margins[side])}
                    onChange={(e) => setMargin(side, e.target.value)}
                    min={0}
                    step={0.25}
                    size="compact"
                  />
                </label>
              ))}
            </div>
          ) : (
            <Chip>{marginSummary}</Chip>
          )}
        </Field>

        {isEditing ? (
          <Field label="Description">
            <TextArea
              value={settings.description}
              onChange={(e) => set('description', e.target.value)}
              rows={2}
            />
          </Field>
        ) : null}
      </div>
    </section>
  );
}
